/**
 * What sign-in methods this deployment offers.
 *
 * The server states it; the console renders exactly that. A button for a provider with no
 * credentials answers with a provider error, which reads as a broken sign-in rather than a
 * feature that was never offered — and the alternative, hard-coding the buttons, is a
 * second copy of the server's configuration that drifts the first time a deployment adds
 * or drops a provider.
 *
 * The fallback while the answer is in flight is deliberately the *narrowest* useful set:
 * email one-time codes, which every deployment that can authenticate at all has. Showing
 * no buttons and then adding them is a moment of emptiness; showing buttons and removing
 * them is a button somebody already clicked.
 *
 * ## The failure is reported, not just absorbed
 *
 * The fallback above is right, but *staying* on it silently is not: when the request
 * fails, a deployment with three providers draws an email-only form and looks like it was
 * never configured for them. That is exactly what a split Wrangler dev registry did —
 * `/v1/auth-methods` answered "Worker layerit-api not found", the screen quietly dropped
 * every provider button, and nothing on the page said why. So the hook returns
 * `unavailable` alongside the methods and the sign-in screen says so.
 */
import { useQuery } from '@tanstack/react-query'
import { queryKeys } from '@web/query'
import { rpc, unwrap } from '@web/rpc'
import type { AuthMethods } from '@api/app'

/** What is offered when the deployment cannot be asked. */
export const NO_METHODS: AuthMethods = { emailOtp: true, passkey: false, social: [] }

async function fetchAuthMethods(): Promise<AuthMethods> {
	return unwrap(await rpc.v1['auth-methods'].$get())
}

export interface AuthMethodsState {
	methods: AuthMethods
	/**
	 * True once the request has failed: the methods are the fallback, not the deployment's
	 * answer. Distinct from "still loading", which is the same list for a moment but is not
	 * a problem to report.
	 */
	unavailable: boolean
}

export function useAuthMethods(): AuthMethodsState {
	/* One attempt, no retry: this is a hint about which buttons to draw, and a slow answer
	 * must not hold up the sign-in form. A failure leaves the narrowest set, which still
	 * lets somebody in. */
	const query = useQuery({
		queryKey: queryKeys.authMethods,
		queryFn: fetchAuthMethods,
		retry: false,
		staleTime: Infinity,
	})

	return { methods: query.data ?? NO_METHODS, unavailable: query.isError }
}
